import { Box, useTheme } from "@mui/material";
import { BarDatum } from "@nivo/bar";
import React, { useEffect, useMemo, useRef, useState } from "react";
import ReactECharts from "echarts-for-react";
import { EChartsOption } from "echarts";
import LegendsCharts from "../../legends";
import useStyles from "../styles";
import handleColors from "../utils/handleColors";
import { MyLineBarDashConfig } from "./types";
import maskUnits from "../../../utils/maskUnits";
import { formatData } from "../../../functions/formatData";
import { accumulatedData } from "../../../functions/formatData/acumulator";
import parseKeyToColomns from "../../../functions/formatData/parseKeytoColumn";
import { shoterNames } from "../../../functions/formatData/shorterNames";
import { getColorsArray } from "../../../utils/getColorsPallet";
import calculateMetrics from "../utils/calculateMetrics";

const roundDownToStep = (value: number, step: number) => {
  return Math.floor(value / step) * step;
};

const getYMinMaxLineBar = (
  data: BarDatum[],
  barKeys: string[],
  lineKey: string,
  groupMode: string | undefined,
  factor = 1,
): { min: number; max: number } => {
  if (!data?.length || (!barKeys?.length && !lineKey))
    return { min: 0, max: 0 };

  // Get bar values
  const barValues = data.flatMap((item) => {
    if (groupMode !== "grouped") {
      const sum = barKeys.reduce(
        (acc, key) => acc + (Number(item[key]) || 0),
        0,
      );
      return [sum];
    } else {
      return barKeys.map((key) => Number(item[key]) || 0);
    }
  });

  // Get line values
  const lineValues = data.map((item) => Number(item[lineKey]) || 0);

  const allValues = [...barValues, ...lineValues];
  const cleanValues = allValues.filter(Number.isFinite);

  const hasNegativeValues = cleanValues.some((value) => value < 0);
  if (!hasNegativeValues) {
    return {
      min: 0,
      max: Math.max(...cleanValues) * factor,
    };
  }

  return {
    min: roundDownToStep(Math.min(...cleanValues) * factor, 5),
    max: Math.max(...cleanValues) * factor,
  };
};

const mapCurveToECharts = (curve?: string) => {
  switch (curve) {
    case "basis":
    case "cardinal":
    case "catmullRom":
    case "monotoneX":
    case "monotoneY":
    case "natural":
      return { smooth: true, step: undefined };
    case "step":
    case "stepBefore":
      return { smooth: false, step: "start" };
    case "stepAfter":
      return { smooth: false, step: "end" };
    case "linear":
    default:
      return { smooth: false, step: undefined };
  }
};

const LineBarChart: React.FC<MyLineBarDashConfig> = (props) => {
  const {
    groupMode,
    units,
    fractionDigits,
    keys,
    colorsByKey,
    keyLine,
    indexBy = "id",
    curve,
    data,
    groupBy = "date",
    interval = "M",
    viewMode,
    groupMethod,
    parseKeyToColumn,
    metricsType,
    enableTotals,
    colors,
  } = props;
  const [boxHeight, setBoxHeight] = useState<number | null>(null);
  const [boxWidth, setBoxWidth] = useState<number | null>(null);

  const lineBarRef = useRef<HTMLDivElement>(null);
  const theme = useTheme();
  const styles = useStyles(theme);

  useEffect(() => {
    const element = lineBarRef.current;
    if (!element) return;

    let resizeTimer: ReturnType<typeof setTimeout>;

    const resizeObserver = new ResizeObserver((entries) => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        window.requestAnimationFrame(() => {
          if (!Array.isArray(entries) || !entries.length) return;
          const entry = entries[0];
          const { width, height } = entry.contentRect;
          if (width > 0 && height > 0) {
            setBoxWidth(width);
            setBoxHeight(height);
          }
        });
      }, 50);
    });

    resizeObserver.observe(element);

    return () => {
      resizeObserver.disconnect();
      clearTimeout(resizeTimer);
    };
  }, []);

  const { formattedData, keyNames, formattedDataAccumulated } = useMemo(() => {
    if (!keys)
      return {
        formattedData: [],
        keyNames: [],
        formattedDataAccumulated: [],
      };
    const { parsedData, newKeys } = parseKeyToColomns(
      [...data],
      keys,
      indexBy,
      parseKeyToColumn,
    );
    const { formattedData, keyNames } = formatData({
      data: parsedData,
      indexBy,
      keys: [...newKeys, keyLine],
      formatSeries: "default",
      groupBy,
      interval,
      groupMethod,
    });
    if (groupBy !== "date") {
      return {
        formattedData: shoterNames(parsedData) as BarDatum[],
        keyNames: newKeys,
        formattedDataAccumulated: [],
      };
    }

    const accumulated =
      (viewMode === "accumulate" &&
        accumulatedData({
          data: formattedData,
          keys,
        })) ||
      [];
    return {
      formattedData,
      keyNames: keyNames.filter((key) => key !== keyLine),
      formattedDataAccumulated: accumulated,
    };
  }, [
    data,
    keys,
    indexBy,
    interval,
    groupBy,
    groupMethod,
    parseKeyToColumn,
    keyLine,
    viewMode,
  ]);

  const activeData =
    viewMode === "accumulate" ? formattedDataAccumulated : formattedData;

  const chartOption: EChartsOption = useMemo(() => {
    const concatKeys = [...keyNames, keyLine];
    let colorPalette: string[] = [];

    if (colorsByKey) {
      const barColors = keyNames.map((key) =>
        handleColors(key, theme, colorsByKey),
      );
      const lineColor = handleColors(keyLine, theme, colorsByKey);
      colorPalette = [...barColors, lineColor];
    } else if (colors) {
      colorPalette = getColorsArray(
        concatKeys.length,
        colors,
        theme.palette.mode === "dark",
      );
    } else {
      colorPalette = getColorsArray(
        concatKeys.length,
        "nivo",
        theme.palette.mode === "dark",
      );
    }

    const { min, max } = getYMinMaxLineBar(
      activeData,
      keyNames,
      keyLine,
      groupMode,
      1.1,
    );

    const { smooth, step } = mapCurveToECharts(curve);
    const showPoint = (boxWidth || 1) / activeData.length > 15;
    const barSeries = keyNames.map((key, idx) => ({
      name: key,
      type: "bar",
      large: true,
      stack: groupMode === "grouped" ? undefined : "total",
      itemStyle: {
        borderRadius:
          groupMode !== "grouped" && idx === keyNames.length - 1
            ? [4, 4, 0, 0]
            : [0, 0, 0, 0],
      },
      label: {
        show: false,
      },
      tooltip: {
        confine: true,
        valueFormatter: (value: number) =>
          maskUnits(value, units, fractionDigits),
      },
      encode: {
        x: indexBy,
        y: key,
      },
    }));

    // Add invisible series for totals on stacked bars
    if (groupMode !== "grouped" && enableTotals) {
      barSeries.push({
        name: "_total_invisible",
        type: "bar",
        stack: "total",
        silent: true,
        itemStyle: {
          color: "transparent",
        },
        label: {
          show: true,
          position: "top",
          color: theme.palette.text.primary,
          fontWeight: "bold",
          formatter: function (params: any) {
            let sum = 0;
            const dataItem = activeData[params.dataIndex];
            for (const k of keyNames) {
              const value = Number(dataItem?.[k]);
              if (!isNaN(value)) sum += value;
            }
            return sum > 0 ? maskUnits(sum, units, fractionDigits) : "";
          },
        },
        labelLayout: {
          hideOverlap: true,
        },
        encode: {
          x: indexBy,
          y: "_invisible_value",
        },
        emphasis: {
          focus: "none",
        },
        tooltip: {
          show: false,
        },
      } as any);

      activeData.forEach((item: any) => {
        item._invisible_value = 0;
      });
    }

    const lineSeries = {
      name: keyLine,
      type: "line",
      smooth: smooth,
      step: step as any,
      symbol: showPoint ? "circle" : "none",
      symbolSize: 8,
      tooltip: {
        valueFormatter: (value: number) =>
          maskUnits(value, units, fractionDigits),
      },
      encode: {
        x: indexBy,
        y: keyLine,
      },
      label: {
        show: showPoint && enableTotals,
        position: "bottom",
        color: theme.palette.text.primary,
        borderRadius: 5,
        fontWeight: "bold",
        padding: [4, 6],
        backgroundColor: colorPalette[colorPalette.length - 1],
        formatter: (params: any) =>
          maskUnits(params.value?.[keyLine], units, fractionDigits),
      },
    };

    return {
      animation: true,
      color: colorPalette,
      grid: {
        top: 30,
        right: 24,
        bottom: 10,
        left: 10,
        containLabel: true,
      },
      tooltip: {
        trigger: "axis",
        axisPointer: { type: "shadow" },
        backgroundColor: theme.palette.background.default,
        textStyle: { color: theme.palette.text.primary },
        confine: true,
      },
      dataset: {
        source: activeData,
      },
      legend: { show: false },
      xAxis: {
        type: "category",
        axisTick: { show: true },
        axisLine: { show: false },
        axisLabel: {
          fontSize: 10,
          color: theme.palette.text.secondary,
          hideOverlap: true,
        },
      },
      yAxis: {
        type: "value",
        min,
        max,
        splitLine: {
          show: true,
          lineStyle: {
            type: "dashed",
            color: theme.palette.divider,
            opacity: 0.5,
          },
        },
        axisLabel: {
          formatter: (value: number) => maskUnits(value, units, 0),
          fontSize: 10,
          color: theme.palette.text.secondary,
        },
      },
      series: [...barSeries, lineSeries] as any,
    };
  }, [
    activeData,
    keyNames,
    keyLine,
    boxWidth,
    colors,
    colorsByKey,
    theme,
    groupMode,
    units,
    fractionDigits,
    indexBy,
    curve,
    enableTotals,
  ]);

  const concatKeys = useMemo(() => [...keyNames, keyLine], [keyNames, keyLine]);
  const isReady = boxHeight && boxWidth;

  return formattedData.length === 0 ? undefined : (
    <Box
      sx={{
        ...styles.boxContainer,
        height: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box
        sx={{
          ...styles.boxLegend,
          minHeight: metricsType ? "45px" : "25px",
          flexShrink: 0,
        }}
      >
        <LegendsCharts
          {...props}
          keys={concatKeys}
          metricsType={
            metricsType
              ? calculateMetrics(metricsType, data as any[])
              : undefined
          }
        />
      </Box>
      <Box
        sx={{
          ...styles.boxContainerSizeChart,
          overflow: "hidden",
          minWidth: 0,
          width: "100%",
          flexGrow: 1,
          height: "100%",
          position: "relative",
        }}
        ref={lineBarRef}
      >
        <Box
          sx={{
            width: "100%",
            height: "100%",
            position: "absolute",
            top: 0,
            left: 0,
          }}
        >
          <ReactECharts
            option={chartOption}
            style={{ height: "100%", width: "100%" }}
            notMerge={true}
            lazyUpdate={true}
            autoResize={true}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default LineBarChart;
